import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faExclamationCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';
import classNames from '../../../utils/classNames';
import { valueToPercentage } from '../../utils';
import { getProgressBarColorClass } from '../utils';

// Sound limit of 140 dB.
const soundLimit = 140;

interface ProgressBarIconProps {
    icon: IconDefinition;
    level: number | null;
    className?: string;
}

function ProgressBarIcon({ icon, level, className }: ProgressBarIconProps): JSX.Element | null {
    if (!level) return null;

    return (
        <div
            className={`absolute flex h-6 border-l border-dashed border-black`}
            style={{
                left: `${valueToPercentage(level, soundLimit)}%`,
            }}
        >
            <div className={'relative -left-[6px] bottom-3 flex items-center gap-1'}>
                <FontAwesomeIcon
                    icon={icon}
                    className={classNames('h-2 w-2 rounded-full p-0.5 text-white', className)}
                    size="sm"
                />
            </div>
        </div>
    );
}

interface ProgressBarProps {
    alarmLevel: number | null;
    warningLevel: number | null;
    measuredValue: string | null;
    isCompact?: boolean;
    unit?: string;
    hasData?: boolean;
}

function ProgressBar({
    alarmLevel,
    warningLevel,
    measuredValue: measuredValueRaw,
    isCompact,
    unit,
    hasData = true,
}: ProgressBarProps): JSX.Element {
    const measuredValue = measuredValueRaw ? parseFloat(measuredValueRaw) : 0;
    const textColorClass = hasData ? 'text-white' : null;
    const conditionalStyles = useMemo(() => {
        const progressBarColorClass = hasData
            ? getProgressBarColorClass(measuredValue, warningLevel, alarmLevel)
            : null;
        return {
            progressBarColorClass,
            textColorClass,
        };
    }, [hasData, measuredValue, warningLevel, alarmLevel, textColorClass]);

    return (
        <>
            <div className="flex h-3.5">
                <div className="bg-gray-outline flex w-full max-w-xs justify-between gap-0.5 rounded">
                    <div
                        className={`${conditionalStyles.progressBarColorClass} text-2xs rounded pl-1 ${conditionalStyles.textColorClass}`}
                        style={{
                            width: `${valueToPercentage(measuredValue, soundLimit)}%`,
                        }}
                    >
                        {!isCompact && (
                            <div className="w-12 text-nowrap">
                                {measuredValueRaw ? `${measuredValueRaw} ${unit}` : ''}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div>
                <div className="relative mt-[-22px] flex h-6 w-full max-w-xs">
                    <ProgressBarIcon
                        icon={faExclamationCircle}
                        className="bg-warning"
                        level={warningLevel}
                    />
                    <ProgressBarIcon
                        icon={faExclamationTriangle}
                        className="bg-error"
                        level={alarmLevel}
                    />
                </div>
            </div>
        </>
    );
}

export default ProgressBar;
